// Helper compartido para generación de PDF con el branding del tenant.
// Se hidrata desde useBranding() y debe ser pasado a cada generador.
//
// Uso:
//   import { renderBrandedHeader, renderBrandedFooter, hexToRgb } from "@/lib/pdfBranding";
//   renderBrandedHeader(doc, branding, { title: "RECIBO", subtitle: "RC-00001" });

export function hexToRgb(hex) {
  const h = (hex || "#1B4332").replace("#", "");
  if (h.length !== 6) return [0, 0, 0];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

// Convertir data-url base64 a formato útil para jsPDF.addImage.
// Devuelve { dataUrl, format } o null.
export function brandingLogoForPdf(branding, slot = "documentos") {
  const candidates = [
    branding[`logo_${slot}_b64`],
    branding.logo_documentos_b64,
    branding.logo_principal_b64,
    branding.logo_cuadrado_b64,
  ].filter(Boolean);
  const data = candidates[0];
  if (!data) return null;
  let format = "PNG";
  if (data.startsWith("data:image/jpeg") || data.startsWith("data:image/jpg")) format = "JPEG";
  else if (data.startsWith("data:image/svg")) format = "SVG"; // jsPDF no soporta SVG nativo, fallback a inicial
  if (format === "SVG") return null;
  return { dataUrl: data, format };
}

// Renderiza el header común con logo + nombre + tagline + datos de la clínica.
// Retorna la nueva Y donde puede continuar el contenido.
export function renderBrandedHeader(doc, branding, { title, subtitle, accent = "right" } = {}) {
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;
  const primary = hexToRgb(branding?.color_primario || "#1B4332");
  doc.setFillColor(...primary);
  doc.rect(0, 0, W, 90, "F");

  const logo = brandingLogoForPdf(branding, "documentos");
  let leftCursor = margin;
  if (logo) {
    try {
      // Normalizamos a 34x34 px max para que quepa en la franja superior
      doc.addImage(logo.dataUrl, logo.format, margin, 22, 46, 46);
      leftCursor = margin + 56;
    } catch {
      // fallback a iniciales
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, 28, 34, 34, 3, 3, "F");
      leftCursor = margin + 50;
    }
  } else {
    // iniciales
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, 28, 34, 34, 3, 3, "F");
    doc.setTextColor(...primary);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    const name = (branding?.nombre_comercial || "DC").trim();
    const initials = name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "DC";
    doc.text(initials, margin + 17 - (initials.length * 3), 50);
    leftCursor = margin + 50;
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold"); doc.setFontSize(18);
  doc.text(branding?.nombre_comercial || "DentCloud", leftCursor, 47);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  if (branding?.eslogan) {
    doc.text(branding.eslogan, leftCursor, 62);
  } else if (subtitle) {
    doc.text(subtitle, leftCursor, 62);
  }
  if (title) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.text(title, W - margin, 47, { align: "right" });
  }
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(new Date().toLocaleDateString("es-CO"), W - margin, 62, { align: "right" });
  if (subtitle && branding?.eslogan) {
    doc.text(subtitle, W - margin, 76, { align: "right" });
  }

  // Datos de contacto debajo del header
  let y = 110;
  doc.setTextColor(110, 110, 110); doc.setFontSize(8);
  const contactLine = [
    branding?.nit && `NIT: ${branding.nit}`,
    branding?.direccion,
    branding?.telefono && `Tel: ${branding.telefono}`,
    branding?.email_contacto,
  ].filter(Boolean).join(" · ");
  if (contactLine) {
    doc.text(contactLine, margin, y);
    y += 14;
  }
  return y + 4;
}

export function renderBrandedFooter(doc, branding) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 48;
  doc.setFontSize(8); doc.setTextColor(110, 110, 110);
  const left = `Generado: ${new Date().toLocaleString("es-CO")} · ${branding?.nombre_comercial || "DentCloud"}`;
  doc.text(left, margin, H - 24);
  if (branding?.footer_recibos) {
    doc.text(branding.footer_recibos, W / 2, H - 24, { align: "center" });
  }
}

// Helper para colores de tabla autoTable consistentes con la paleta.
export function brandedTableHeadStyles(branding) {
  return { fillColor: hexToRgb(branding?.color_primario || "#1B4332"), textColor: 255, fontSize: 9 };
}

/**
 * Dibuja un bloque de firma con (opcionalmente) imagen de firma manuscrita por encima
 * de la línea de firma + nombre + texto debajo. Devuelve la nueva Y debajo del bloque.
 *
 * - doc: jsPDF instance
 * - x, baselineY: coordenadas. La línea horizontal queda en baselineY.
 * - width: ancho del bloque
 * - signatureImage: data URL PNG/JPG (opcional)
 * - label: texto debajo de la línea, en negrita (ej. "Firma del profesional")
 * - name: nombre completo (ej. "Dra. María Pérez")
 * - subtitle: texto auxiliar pequeño debajo (ej. registro profesional)
 */
export function drawSignatureBlock(doc, { x, baselineY, width, signatureImage, label, name, subtitle }) {
  // Imagen de firma encima de la línea (si hay)
  if (signatureImage) {
    try {
      let format = "PNG";
      if (signatureImage.startsWith("data:image/jpeg") || signatureImage.startsWith("data:image/jpg")) format = "JPEG";
      const sigH = 36;
      const sigW = Math.min(width - 8, 130);
      const imgX = x + (width - sigW) / 2;
      const imgY = baselineY - sigH - 2;
      doc.addImage(signatureImage, format, imgX, imgY, sigW, sigH, undefined, "FAST");
    } catch { /* ignorar */ }
  }
  // Línea
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.5);
  doc.line(x, baselineY, x + width, baselineY);
  // Label
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(26, 28, 26);
  doc.text(label || "", x, baselineY + 14);
  // Nombre
  if (name) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(110, 115, 110);
    doc.text(String(name), x, baselineY + 26);
  }
  if (subtitle) {
    doc.setFontSize(7);
    doc.text(String(subtitle), x, baselineY + 36);
  }
  return baselineY + (subtitle ? 40 : 30);
}

